/**
 * Client for managing Imports
 */
import { ExaError, HttpStatusCode } from "../errors";
import { PaginationParams, WebsetsBaseClient } from "./base";
import {
  CreateImportParameters,
  CreateImportParametersFormat,
  CreateImportResponse,
  Import,
  ImportStatus,
  ListImportsResponse,
  UpdateImport,
} from "./openapi";

/**
 * Options for waiting until import completion
 */
export interface WaitUntilCompletedOptions {
  /**
   * Maximum time to wait in milliseconds (default: 5 minutes)
   */
  timeout?: number;
  /**
   * How often to poll for status in milliseconds (default: 2 seconds)
   */
  pollInterval?: number;
  /**
   * Callback function called on each poll with the current status
   */
  onPoll?: (status: ImportStatus) => void;
}

/**
 * Parameters for creating an import with CSV data
 */
export interface CreateImportWithCsvParameters {
  /**
   * Title of the import
   */
  title: string;
  /**
   * Entity type and configuration
   */
  entity: CreateImportParameters["entity"];
  /**
   * Optional metadata
   */
  metadata?: CreateImportParameters["metadata"];
  /**
   * Optional CSV-specific parameters
   */
  csv?: CreateImportParameters["csv"];
}

/**
 * CSV data input - can be raw data or buffer
 */
export type CsvDataInput = string | Buffer;

/**
 * Client for managing Imports
 */
export class ImportsClient extends WebsetsBaseClient {
  /**
   * Create a new Import (basic version - returns upload URL)
   * @param params The import creation parameters
   * @returns The created Import response with upload URL
   */
  async create(params: CreateImportParameters): Promise<CreateImportResponse>;

  /**
   * Create a new Import with CSV data (handles upload)
   * @param params The import creation parameters (without size/count - calculated automatically)
   * @param csv CSV data as string or Buffer
   * @returns The Import after upload (not waited for completion)
   */
  async create(
    params: CreateImportWithCsvParameters,
    csv: CsvDataInput
  ): Promise<Import>;

  async create(
    params: CreateImportParameters | CreateImportWithCsvParameters,
    csv?: CsvDataInput
  ): Promise<CreateImportResponse | Import> {
    if (csv === undefined) {
      return this.request<CreateImportResponse>(
        "/v0/imports",
        "POST",
        params as CreateImportParameters
      );
    }

    let csvBuffer: Buffer;
    if (typeof csv === "string") {
      csvBuffer = Buffer.from(csv, "utf8");
    } else if (Buffer.isBuffer(csv)) {
      csvBuffer = csv;
    } else {
      throw new ExaError(
        "Invalid CSV data input. Must be string or Buffer",
        HttpStatusCode.BadRequest
      );
    }

    const sizeInBytes = csvBuffer.length;
    const sizeInMB = Math.max(1, Math.ceil(sizeInBytes / (1024 * 1024)));

    const csvText = csvBuffer.toString("utf8");
    const lines = csvText.split("\n").filter((line) => line.trim().length > 0);
    const recordCount = Math.max(0, lines.length - 1); // Subtract 1 for header

    if (sizeInMB > 50) {
      throw new ExaError(
        `CSV file too large: ${sizeInMB}MB. Maximum size is 50MB.`,
        HttpStatusCode.BadRequest
      );
    }

    if (recordCount === 0) {
      throw new ExaError(
        "CSV file appears to have no data records (only header or empty)",
        HttpStatusCode.BadRequest
      );
    }

    const createParams: CreateImportParameters = {
      title: params.title,
      format: CreateImportParametersFormat.csv,
      entity: params.entity,
      size: sizeInBytes,
      count: recordCount,
      metadata: params.metadata,
      csv: (params as CreateImportWithCsvParameters).csv,
    };

    const importResponse = await this.request<CreateImportResponse>(
      "/v0/imports",
      "POST",
      createParams
    );

    try {
      const uploadResponse = await fetch(importResponse.uploadUrl, {
        method: "PUT",
        body: csvBuffer as BodyInit,
      });

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        throw new ExaError(
          `Upload failed: ${uploadResponse.status} ${uploadResponse.statusText}. ${errorText}`,
          HttpStatusCode.BadRequest
        );
      }
    } catch (error) {
      if (error instanceof ExaError) {
        throw error;
      }
      throw new ExaError(
        `Failed to upload CSV data: ${(error as Error).message}`,
        HttpStatusCode.BadRequest
      );
    }

    return importResponse;
  }

  /**
   * Get an Import by ID
   * @param id The ID of the Import
   * @returns The Import
   */
  async get(id: string): Promise<Import> {
    return this.request<Import>(`/v0/imports/${id}`, "GET");
  }

  /**
   * List all Imports
   * @param options Pagination options
   * @returns The list of Imports
   */
  async list(options?: PaginationParams): Promise<ListImportsResponse> {
    const params = this.buildPaginationParams(options);
    return this.request<ListImportsResponse>(
      "/v0/imports",
      "GET",
      undefined,
      params
    );
  }

  /**
   * Update an Import
   * @param id The ID of the Import
   * @param params The import update parameters
   * @returns The updated Import
   */
  async update(id: string, params: UpdateImport): Promise<Import> {
    return this.request<Import>(`/v0/imports/${id}`, "PATCH", params);
  }

  /**
   * Delete an Import
   * @param id The ID of the Import
   * @returns The deleted Import
   */
  async delete(id: string): Promise<Import> {
    return this.request<Import>(`/v0/imports/${id}`, "DELETE");
  }

  /**
   * Wait until an Import is completed or failed
   * @param id The ID of the Import
   * @param options Configuration options for timeout and polling
   * @returns The Import once it reaches a final state (completed or failed)
   * @throws Error if the Import does not complete within the timeout or fails
   */
  async waitUntilCompleted(
    id: string,
    options?: WaitUntilCompletedOptions
  ): Promise<Import> {
    const timeout = options?.timeout ?? 30 * 60 * 1000;
    const pollInterval = options?.pollInterval ?? 2_000;
    const onPoll = options?.onPoll;

    const startTime = Date.now();

    while (true) {
      const importItem = await this.get(id);

      if (onPoll) {
        onPoll(importItem.status);
      }

      if (importItem.status === ImportStatus.completed) {
        return importItem;
      }

      if (importItem.status === ImportStatus.failed) {
        throw new ExaError(
          `Import ${id} failed: ${importItem.failedMessage || "Unknown error"}`,
          HttpStatusCode.BadRequest
        );
      }

      if (Date.now() - startTime > timeout) {
        throw new ExaError(
          `Import ${id} did not complete within ${timeout}ms. Current status: ${importItem.status}`,
          HttpStatusCode.RequestTimeout
        );
      }

      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }
  }

  /**
   * Iterate through all Imports, handling pagination automatically
   * @param options Pagination options
   * @returns Async generator of Imports
   */
  async *listAll(options?: PaginationParams): AsyncGenerator<Import> {
    let cursor: string | undefined = undefined;
    const pageOptions = options ? { ...options } : {};

    while (true) {
      pageOptions.cursor = cursor;
      const response = await this.list(pageOptions);

      for (const importItem of response.data) {
        yield importItem;
      }

      if (!response.hasMore || !response.nextCursor) {
        break;
      }

      cursor = response.nextCursor;
    }
  }

  /**
   * Collect all Imports into an array
   * @param options Pagination options
   * @returns Promise resolving to an array of all Imports
   */
  async getAll(options?: PaginationParams): Promise<Import[]> {
    const imports: Import[] = [];
    for await (const importItem of this.listAll(options)) {
      imports.push(importItem);
    }
    return imports;
  }
}
